import React, { useState, useEffect } from 'react';
import {
  CpuChipIcon,
  ServerIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  CircleStackIcon
} from '@heroicons/react/24/outline';
import MySQLService, { MySQLStatus } from '../services/mysqlService';
import { getEmailJSKeys, isEmailConfigured } from '../config/emailConfig';

interface SystemMonitorProps {
  className?: string;
  variant?: 'panel' | 'inline';
}

interface SystemStatus {
  cpu: number;
  memory: number;
  disk: number;
  mysql: MySQLStatus;
  email: { connected: boolean; source: 'gmail' | 'emailjs' | 'none' };
  aiConnected: boolean;
  uptime: { days: number; hours: number; minutes: number };
  status: 'healthy' | 'warning' | 'error';
}

const toSpanishStatus = (status: 'healthy' | 'warning' | 'error') => {
  switch (status) {
    case 'healthy':
      return 'saludable';
    case 'warning':
      return 'advertencia';
    case 'error':
      return 'error';
  }
};

const formatUptimeLong = (u: SystemStatus['uptime']) => {
  const parts: string[] = [];
  if (u.days) parts.push(`${u.days} ${u.days === 1 ? 'día' : 'días'}`);
  if (u.hours || parts.length) parts.push(`${u.hours} ${u.hours === 1 ? 'hora' : 'horas'}`);
  parts.push(`${u.minutes} ${u.minutes === 1 ? 'minuto' : 'minutos'}`);
  return parts.join(' ');
};

export const SystemMonitor: React.FC<SystemMonitorProps> = ({ className = '', variant = 'panel' }) => {
  const [systemStatus, setSystemStatus] = useState<SystemStatus>({
    cpu: 0,
    memory: 0,
    disk: 0,
    mysql: { connected: false, status: 'disconnected', message: 'Sin conexión', timestamp: new Date() },
    email: { connected: false, source: 'none' },
    aiConnected: false,
    uptime: { days: 0, hours: 0, minutes: 0 },
    status: 'healthy'
  });

  const [isExpanded, setIsExpanded] = useState(false);

  // Simulate system monitoring
  useEffect(() => {
    const updateStatus = async () => {
      const cpu = Math.floor(Math.random() * 100);
      const memory = Math.floor(Math.random() * 100);
      const disk = Math.floor(Math.random() * 100);
      const days = Math.floor(Math.random() * 30);
      const hours = Math.floor(Math.random() * 24);
      const minutes = Math.floor(Math.random() * 60);
      const uptime = { days, hours, minutes };

      // Check MySQL connection status
      const mysqlService = MySQLService.getInstance();
      const mysqlStatus = await mysqlService.checkConnection();

      // Email connectivity (heurística local):
      // - Gmail: si existe gmailConfig con user y appPassword
      // - EmailJS: si están guardadas las claves
      // Nota: no se hace ping al backend aquí para evitar latencia
      let emailConnected = false as boolean;
      let emailSource: 'gmail' | 'emailjs' | 'none' = 'none';
      try {
        const gmailRaw = localStorage.getItem('gmailConfig');
        if (gmailRaw) {
          const gmail = JSON.parse(gmailRaw);
          if (gmail?.user && gmail?.appPassword) {
            emailConnected = true; emailSource = 'gmail';
          }
        }
        if (!emailConnected && isEmailConfigured()) {
          const keys = getEmailJSKeys();
          if (keys.publicKey && keys.serviceId) {
            emailConnected = true; emailSource = 'emailjs';
          }
        }
      } catch {}

      // AI connectivity (NVIDIA vía backend)
      let aiConnected = false;
      try {
        const { apiService } = await import('../services/apiService');
        const aiStatus = await apiService.getAiStatus();
        aiConnected = !!aiStatus.data?.configured && aiStatus.data?.ok !== false;
      } catch {}

      let status: 'healthy' | 'warning' | 'error' = 'healthy';
      if (cpu > 80 || memory > 85 || disk > 90 || mysqlStatus.status === 'error') status = 'warning';
      if (cpu > 95 || memory > 95 || disk > 95 || mysqlStatus.status === 'disconnected') status = 'error';

      setSystemStatus({ cpu, memory, disk, mysql: mysqlStatus, email: { connected: emailConnected, source: emailSource }, aiConnected, uptime, status });
    };

    updateStatus();
    const interval = setInterval(updateStatus, 5000);
    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return 'text-green-600 bg-green-100';
      case 'warning':
        return 'text-yellow-600 bg-yellow-100';
      case 'error':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getDotColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return 'bg-green-500';
      case 'warning':
        return 'bg-yellow-500';
      case 'error':
        return 'bg-red-500';
      default:
        return 'bg-gray-400';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <CheckCircleIcon className="h-4 w-4" />;
      case 'warning':
        return <ExclamationTriangleIcon className="h-4 w-4" />;
      case 'error':
        return <ExclamationTriangleIcon className="h-4 w-4" />;
      default:
        return <CheckCircleIcon className="h-4 w-4" />;
    }
  };

  const getUsageColor = (usage: number) => {
    if (usage < 50) return 'text-green-600';
    if (usage < 80) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getMySQLColor = (status: string) => {
    switch (status) {
      case 'connected':
        return 'text-green-600';
      case 'error':
        return 'text-yellow-600';
      case 'disconnected':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  if (variant === 'inline') {
    return (
      <div className={`flex items-center space-x-4 text-xs ${className}`}>
        <div className="flex items-center space-x-1">
          <CpuChipIcon className="h-3.5 w-3.5 text-gray-500" />
          <span className={getUsageColor(systemStatus.cpu)}>CPU {systemStatus.cpu}%</span>
        </div>
        <div className="flex items-center space-x-1">
          <CircleStackIcon className="h-3.5 w-3.5 text-gray-500" />
          <span className={getUsageColor(systemStatus.memory)}>RAM {systemStatus.memory}%</span>
        </div>
        <div className="flex items-center space-x-1">
          <CircleStackIcon className="h-3.5 w-3.5 text-gray-500" />
          <span className={getMySQLColor(systemStatus.mysql.status)}>MySQL {systemStatus.mysql.connected ? '✓' : '✗'}</span>
        </div>
        <div className="flex items-center space-x-1">
          <span className={`w-2 h-2 rounded-full ${systemStatus.email.connected ? 'bg-green-500' : 'bg-red-500'}`}></span>
          <span className={`${systemStatus.email.connected ? 'text-green-600' : 'text-red-600'}`}>
            Mail {systemStatus.email.connected ? '✓' : '✗'}
          </span>
        </div>
        <div className="flex items-center space-x-1">
          <span className={`w-2 h-2 rounded-full ${systemStatus.aiConnected ? 'bg-green-500' : 'bg-red-500'}`}></span>
          <span className={`${systemStatus.aiConnected ? 'text-green-600' : 'text-red-600'}`}>
            IA {systemStatus.aiConnected ? '✓' : '✗'}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg shadow-sm border ${className}`}>
      {/* Compact Header */}
      <div 
        className="p-3 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <ServerIcon className="h-4 w-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Sistema</span>
            <div className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(systemStatus.status)}`}>
              <div className="flex items-center space-x-1">
                {getStatusIcon(systemStatus.status)}
                <span className="capitalize">{toSpanishStatus(systemStatus.status)}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-4 text-xs text-gray-500">
            <div className="flex items-center space-x-1">
              <CpuChipIcon className="h-3 w-3" />
              <span className={getUsageColor(systemStatus.cpu)}>{systemStatus.cpu}%</span>
            </div>
            <div className="flex items-center space-x-1">
              <ClockIcon className="h-3 w-3" />
              <span>{formatUptimeLong(systemStatus.uptime)}</span>
            </div>
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="border-t border-gray-200 p-3 space-y-3">
          {/* CPU Usage */}
          <div className="space-y-1">
            <div className="flex justify-between text-xs">
              <span className="text-gray-600">CPU</span>
              <span className={getUsageColor(systemStatus.cpu)}>{systemStatus.cpu}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all duration-300 ${
                  systemStatus.cpu < 50 ? 'bg-green-500' :
                  systemStatus.cpu < 80 ? 'bg-yellow-500' : 'bg-red-500'
                }`}
                style={{ width: `${systemStatus.cpu}%` }}
              ></div>
            </div>
          </div>

          {/* Memory Usage */}
          <div className="space-y-1">
            <div className="flex justify-between text-xs">
              <span className="text-gray-600">Memoria</span>
              <span className={getUsageColor(systemStatus.memory)}>{systemStatus.memory}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all duration-300 ${
                  systemStatus.memory < 50 ? 'bg-green-500' :
                  systemStatus.memory < 80 ? 'bg-yellow-500' : 'bg-red-500'
                }`}
                style={{ width: `${systemStatus.memory}%` }}
              ></div>
            </div>
          </div>

          {/* Disk Usage */}
          <div className="space-y-1">
            <div className="flex justify-between text-xs">
              <span className="text-gray-600">Disco</span>
              <span className={getUsageColor(systemStatus.disk)}>{systemStatus.disk}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all duration-300 ${
                  systemStatus.disk < 50 ? 'bg-green-500' :
                  systemStatus.disk < 80 ? 'bg-yellow-500' : 'bg-red-500'
                }`}
                style={{ width: `${systemStatus.disk}%` }}
              ></div>
            </div>
          </div>

          {/* MySQL Status */}
          <div className="space-y-1">
            <div className="flex justify-between text-xs">
              <span className="text-gray-600">MySQL</span>
              <span className={getMySQLColor(systemStatus.mysql.status)}>
                {systemStatus.mysql.connected ? 'Conectado' : 'Desconectado'}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <div className={`w-3 h-3 rounded-full ${
                systemStatus.mysql.connected ? 'bg-green-500' : 'bg-red-500'
              }`}></div>
              <span className="text-xs text-gray-500">{systemStatus.mysql.message}</span>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="pt-2 border-t border-gray-100">
            <div className="flex space-x-2">
              <button className="text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-2 py-1 rounded transition-colors">
                Ver Detalles
              </button>
              <button className="text-xs text-gray-600 hover:text-gray-800 hover:bg-gray-50 px-2 py-1 rounded transition-colors">
                Historial
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}; 